import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Top-level error boundary. Wraps the entire app so a single bad render
 * doesn't blank the screen. Logs to console (Sentry/PostHog hookup is a
 * Phase 9 follow-up — for now we just keep the user from seeing a blank
 * page and give them an "Reload" out).
 */
type Props = { children: ReactNode };
type State = { hasError: boolean; message: string | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  override render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-center">
        <div className="max-w-md space-y-3">
          <h1 className="font-display text-2xl font-bold">Something went wrong.</h1>
          <p className="text-sm text-muted-foreground">
            EduBorg hit an unexpected error. The team has been notified.
            {this.state.message && (
              <span className="mt-2 block font-mono text-xs">{this.state.message}</span>
            )}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
